// ============================================================================
// useLayoutPreferences
// ============================================================================
// Resolves the UI_PREFERENCES keys that control main-layout chrome.
//
// Ported from EnterpriseTemplates react-project-template (Release 1.3.1). Keep in step with the
// template; only the fetch (CRS configService shape) and the cache key (STORAGE_KEYS, so logout
// clears it) differ. crs-db seed/055 seeds both keys FALSE: CRS takes the entity, business unit
// and financial year from the property and the business date (settings cleanup C15).
//
// Separate from useConfigSettings, which carries the whole Settings-screen
// edit model (scope picker, staged edits, save/discard). The layout needs two
// booleans, so it gets a hook that fetches once and holds nothing.

import { useEffect, useState } from 'react';
import { configService } from '../../api/services/configService';
import { STORAGE_KEYS } from '../../auth/storageKeys';

export interface LayoutPreferences {
  showEntityBu: boolean;
  showFinancialYear: boolean;
  /** True until the first resolve settles. */
  loading: boolean;
}

/**
 * Defaults are TRUE and are applied when the fetch FAILS — never before it
 * settles.
 *
 * Failing OPEN is deliberate, and is the opposite of the rule for permissions.
 * A permission that cannot be resolved must deny — showing data to someone
 * unauthorised is the harm. A layout flag that cannot be resolved must SHOW:
 * hiding the entity selector on a config outage would silently pin every user
 * to their default entity, so they would read another entity's data with
 * nothing on screen indicating it. A redundant selector is visible and
 * harmless; a missing one is invisible and wrong.
 *
 * CALLERS MUST GATE ON `loading`. Applying these defaults optimistically while
 * the fetch is in flight paints both selectors and then removes them a beat
 * later — visible on a fast connection, and several seconds of flicker plus a
 * layout shift on a slow one. Fail-open is the answer to "the fetch failed",
 * not to "the fetch has not finished".
 */
const DEFAULTS: Omit<LayoutPreferences, 'loading'> = {
  showEntityBu: true,
  showFinancialYear: true,
};

const asBool = (raw: string | null | undefined, fallback: boolean): boolean => {
  if (raw === null || raw === undefined || raw === '') return fallback;
  const v = String(raw).trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
};

/**
 * Last resolved values, cached so the NEXT mount can paint the final layout
 * immediately instead of painting once and re-rendering when the fetch lands.
 *
 * That re-render is what moved the notification and persona controls
 * horizontally on every load. Gating on `loading` stopped the selectors
 * flashing but not the shift, because the shift comes from the layout
 * re-rendering at all — not from what it rendered first.
 *
 * localStorage, not sessionStorage: the value must survive a refresh, which is
 * when the shift was most visible. It is wiped by performLogout along with
 * every other key, which is correct — configuration is tenant-scoped and must
 * not survive into a different tenant's session.
 *
 * This is a PAINT HINT, never the source of truth. The fetch still runs and
 * overwrites it. A stale hint costs one corrected re-render, which is exactly
 * what happened before caching, so the worst case is no worse than the old
 * behaviour and the common case has no shift at all.
 */
const CACHE_KEY = STORAGE_KEYS.LAYOUT_PREFERENCES;

const readCache = (): Omit<LayoutPreferences, 'loading'> | null => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.showEntityBu !== 'boolean' || typeof parsed?.showFinancialYear !== 'boolean') {
      return null;
    }
    return { showEntityBu: parsed.showEntityBu, showFinancialYear: parsed.showFinancialYear };
  } catch {
    return null;
  }
};

const writeCache = (value: Omit<LayoutPreferences, 'loading'>): void => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // Private mode or a full quota. The hook still works; only the paint hint
    // is lost, so the next load behaves as it did before caching.
  }
};

export const useLayoutPreferences = (): LayoutPreferences => {
  // Read synchronously in the initialiser, NOT in an effect: an effect runs
  // after the first paint, which is the very thing being avoided.
  const [prefs, setPrefs] = useState<LayoutPreferences>(() => {
    const cached = readCache();
    return cached
      ? { ...cached, loading: false }   // first paint is already final
      : { ...DEFAULTS, loading: true }; // first ever load: gate until resolved
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const values = (await configService.getEffectiveConfig('UI_PREFERENCES')).data ?? [];
        if (cancelled) return;

        const find = (key: string) => values.find((v) => v.configKey === key)?.effectiveValue;

        const resolved = {
          showEntityBu: asBool(find('UI.LAYOUT.SHOW_ENTITY_BU'), DEFAULTS.showEntityBu),
          showFinancialYear: asBool(find('UI.LAYOUT.SHOW_FINANCIAL_YEAR'), DEFAULTS.showFinancialYear),
        };

        writeCache(resolved);
        setPrefs({ ...resolved, loading: false });
      } catch {
        // Config unreachable, or the caller lacks UI.CONFIG_SETTINGS.VIEW.
        // Both are ordinary states, not errors worth a toast.
        //
        // Do NOT clear the cache here: a transient outage would then wipe a
        // correct hint and reintroduce the shift on the next load. A stale hint
        // beats no hint, and the next successful fetch overwrites it.
        if (!cancelled) setPrefs((current) => ({ ...current, loading: false }));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return prefs;
};

export default useLayoutPreferences;
